import { css } from '@styled-system/css';
import { center } from '@styled-system/patterns';
import { token } from '@styled-system/tokens';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type Location, useLocation, useNavigate, useParams } from 'react-router-dom';
import { PuffLoader } from 'react-spinners';

import amplitude from '@/amplitude';
import { AnalysisMenuList } from '@/features/analysisMenu/components/AnalysisMenuList';
import { CreateAnalysisModal } from '@/features/analysisMenu/components/CreateAnalysisModal';
import { PublicAnalysisMenuList } from '@/features/analysisMenu/components/PublicAnalysisMenuList';
import { useAnalysisMenuStore } from '@/features/analysisMenu/stores/analysis-menu.store';
import type { AnalysisItemData } from '@/features/analysisMenu/types';
import { useExportAnalysisStats } from '@/features/analysisPage/hooks/useExportAnalysisStats';
import { useUserAnalysisQuotas } from '@/features/analysisPage/hooks/useUserAnalysisQuotas';
import { AppLayout } from '@/shared/components/layout/AppLayout';
import { Message } from '@/shared/components/ui/Message';
import { useDeleteAnalysis, useRenameAnalysis } from '@/shared/hooks/analysis/mutations';
import { useIsMobile } from '@/shared/hooks/useIsMobile';
import type { AnalysisType } from '@/shared/types/analysis';

import { AnalysisPage } from './AnalysisPage';

interface AnalysisPageConfig {
  type: AnalysisType;
  baseRoute: string;
}

// Déduire la configuration à partir du type (clé i18n traduite au render)
const getConfigFromType = (type: AnalysisType) => {
  switch (type) {
    case 'product':
    case 'vendor':
      return {
        emptyMessageKey: 'analysisMenu.emptyCreatePrompt',
        showCreateButton: true,
      };
    case 'public':
      return {
        emptyMessageKey: 'analysisMenu.noPublicAvailable',
        showCreateButton: false,
      };
    default:
      return {
        emptyMessageKey: 'analysisMenu.noneAvailable',
        showCreateButton: false,
      };
  }
};

export const createAnalysisPage = ({ type, baseRoute }: AnalysisPageConfig): FC => {
  const AnalysisPageComponent: FC = () => {
    const location: Location<{
      fromHome?: boolean;
    } | null> = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const { id: analysisId } = useParams<{ id: string }>();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const isMobile = useIsMobile();

    // Store pour l'état partagé
    const { isLoading: menuStoreLoading, isEmpty, error } = useAnalysisMenuStore();

    // Hook pour récupérer les quotas utilisateur
    const { quotas, loading: quotasLoading } = useUserAnalysisQuotas();

    // Loading status
    const isLoading = menuStoreLoading || quotasLoading;

    const config = getConfigFromType(type);

    // Vérifier les permissions de création d'analyse
    const canCreateAnalysis =
      quotas &&
      ((type === 'product' && quotas.canCreateProductAnalysis) ||
        (type === 'vendor' && quotas.canCreateVendorAnalysis));
    const analysesMax =
      (type === 'product' ? quotas?.productAnalysesMax : type === 'vendor' ? quotas?.vendorAnalysesMax : null) ?? 0;
    const noSubscriptionActive = !!quotas && quotas.productAnalysesMax === 0 && quotas.vendorAnalysesMax === 0;

    // Redirection automatique vers /analysis/public si on viens de / et que le menu est vide
    useEffect(() => {
      if (type !== 'public' && location.state?.fromHome && !isLoading && isEmpty) {
        navigate('/analysis/public');
      }
    }, [location, isLoading, isEmpty, navigate]);

    // Hooks centralisés pour les mutations
    const { renameAnalysis } = useRenameAnalysis();
    const { deleteAnalysis } = useDeleteAnalysis();
    const { exportAnalysisStats } = useExportAnalysisStats();

    const handleAnalysisClick = (analysis: AnalysisItemData) => {
      // Tracker l'analyse cliquée
      if (type === 'product') {
        amplitude.track('Produits Item Clicked', { analysisId: analysis.id });
      } else if (type === 'vendor') {
        amplitude.track('Vendeurs Item Clicked', { analysisId: analysis.id });
      } else if (type === 'public') {
        amplitude.track('Publiques Item Clicked', { analysisId: analysis.id });
      }

      // Naviguer vers la page de l'analyse
      navigate(`${baseRoute}/${analysis.id}`);
    };

    const handleAutoRedirect = (analysisId: string) => {
      // Ne pas rediriger automatiquement sur mobile
      if (!isMobile) {
        navigate(`${baseRoute}/${analysisId}`);
      }
    };

    const navigateToSubscription = () => {
      navigate('/subscription');
    };

    const handleCreateNew = () => {
      if (!canCreateAnalysis) {
        // Rediriger vers la page d'abonnement si l'utilisateur n'a pas les permissions
        return navigateToSubscription();
      }

      if (type === 'product') {
        amplitude.track('Produits Add Started');
      } else if (type === 'vendor') {
        amplitude.track('Vendeurs Add Started');
      }

      setIsModalOpen(true);
    };

    const handleModalClose = () => {
      setIsModalOpen(false);
    };

    const handleModalSuccess = (analysisId?: string) => {
      // Si on a un ID d'analyse, naviguer vers cette analyse
      if (analysisId) {
        navigate(`${baseRoute}/${analysisId}`);
      }
    };

    const handleRename = async (analysisId: string, newName: string) => {
      try {
        await renameAnalysis(analysisId, newName);
      } catch {
        // L'erreur est déjà gérée dans le hook
      }
    };


    const handleDelete = async (analysisToDeleteId: string) => {
      try {
        await deleteAnalysis(analysisToDeleteId);

        // Si on supprime l'analyse actuellement affichée, naviguer vers la liste
        if (analysisId && analysisId === analysisToDeleteId) {
          navigate(baseRoute);
        }
      } catch {
        // L'erreur est déjà gérée dans le hook
      }
    };

    const handleExport = async () => {
      // Appeler la fonction d'export
      await exportAnalysisStats();
    };

    const handleBackToMenu = () => {
      navigate(baseRoute);
    };

    // Styles
    const contentStyles = css({
      p: '2rem',
      color: 'white.default',
    });

    // Menu selon le type d'analyse
    // Sur mobile, ne pas afficher le menu si on est sur une analyse spécifique
    const shouldShowMenu = !isMobile || !analysisId;
    const menu = shouldShowMenu ? (
      type === 'public' ? (
        <PublicAnalysisMenuList
          onAnalysisClick={handleAnalysisClick}
          selectedAnalysisId={analysisId}
          onAutoRedirect={handleAutoRedirect}
          isLoading={quotasLoading}
          noSubscriptionActive={noSubscriptionActive}
          navigateToSubscription={navigateToSubscription}
        />
      ) : (
        <AnalysisMenuList
          type={type}
          selectedAnalysisId={analysisId}
          analysesMax={analysesMax}
          noSubscriptionActive={noSubscriptionActive}
          onCreateNew={config.showCreateButton ? handleCreateNew : undefined}
          onAnalysisClick={handleAnalysisClick}
          onRename={handleRename}
          onDelete={handleDelete}
          handleExport={handleExport}
          onAutoRedirect={handleAutoRedirect}
          navigateToSubscription={navigateToSubscription}
          isLoading={quotasLoading}
        />
      )
    ) : null;

    // Contenu principal
    let content;

    if (analysisId) {
      // Page de détail d'une analyse
      content = <AnalysisPage analysisId={analysisId} onBackToMenu={isMobile ? handleBackToMenu : undefined} />;
    } else if (isLoading) {
      // État de chargement
      content = (
        <div
          className={center({
            w: 'full',
            h: 'full',
          })}
        >
          <PuffLoader color={token.var('colors.accent.primary')} size={60} />
        </div>
      );
    } else if (error) {
      // État d'erreur
      content = (
        <div className={contentStyles}>
          <Message type="error" bordered>
            {error}
          </Message>
        </div>
      );
    } else {
      // Liste vide ou avec des analyses
      content = (
        <div className={contentStyles}>
          {!canCreateAnalysis ? (
            type === 'public' ? (
              <Message type="info" bordered>
                {t('analysisMenu.publicEmptyTitle')}
                <br />
                {t('analysisMenu.publicEmptyHint')}
              </Message>
            ) : (
              <div
                onClick={navigateToSubscription}
                className={css({
                  cursor: 'pointer',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                })}
              >
                <Message type="info" bordered textColor="font.primary">
                  {t('analysisMenu.subscribePrompt')}
                </Message>
              </div>
            )
          ) : isEmpty ? (
            <Message type="info" bordered>
              {t(config.emptyMessageKey)}
            </Message>
          ) : null}
        </div>
      );
    }

    const mainContainerStyles = css({
      display: 'flex',
      flexDirection: 'row',
      w: 'full',
      h: 'full',
      overflow: 'hidden',
    });

    const menuSidebarStyles = css({
      w: '17rem',
      h: 'full',
    });

    const menuFullStyles = css({
      px: '0.5rem',
      w: 'full',
      h: 'full',
    });

    return (
      <>
        <AppLayout>
          <div className={mainContainerStyles}>
            {isMobile && shouldShowMenu && <div className={menuFullStyles}>{menu}</div>}
            {!isMobile && menu && <div className={menuSidebarStyles}>{menu}</div>}
            {(!isMobile || !shouldShowMenu) && content}
          </div>
        </AppLayout>

        {config.showCreateButton && (
          <CreateAnalysisModal
            isOpen={isModalOpen}
            onClose={handleModalClose}
            type={type}
            onSuccess={handleModalSuccess}
          />
        )}
      </>
    );
  };

  // Set display name for debugging
  AnalysisPageComponent.displayName = `AnalysisPage(${type})`;

  return AnalysisPageComponent;
};
